import {
    type StripeSubscriptionSummary,
    SubscriptionProductId,
} from '@fruitful/types';
import { useFeatureGate } from '@statsig/expo-bindings';
import { useQueryClient } from '@tanstack/react-query';
import { differenceInCalendarDays } from 'date-fns';
import { useState } from 'react';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FruitfulCustomIcon,
    NavigationRow,
    RowList,
    ScreenContainer,
    Spinner,
    useToastController,
    YStack,
} from 'smoothie';

import PaymentMethodActions from '../../billing/components/PaymentMethodActions';
import PaymentMethodDeleteSheet from '../../billing/components/PaymentMethodDeleteSheet';
import { useGetPaymentMethod } from '../../billing/hooks/usePaymentMethod';
import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { convertToDate, format } from '../../common/utils/dateUtils';
import { FeatureFlags } from '../../feature-flags/FeatureFlags';
import {
    SUCCESS_TOAST_DURATION,
    SuccessToastIcon,
} from '../../member-profile/components/SuccessToast';
import { useBillingInvoicePages } from '../hooks/useBillingInvoicePages';
import useGetSubscription from '../hooks/useGetSubscription';
import { getMemberSinceDate } from '../types/SubscriptionSummary';
import { BillingInvoiceRow } from './BillingInvoiceRow';

const getMembershipEndDate = (
    subscription: StripeSubscriptionSummary,
): string | null => {
    if (
        subscription.cancelAtPeriodEnd &&
        subscription.cancelAtDate != null &&
        subscription.cancelAtDate !== ''
    ) {
        return subscription.cancelAtDate;
    }

    const productId = subscription.product?.id ?? subscription.type;
    const nextProductId =
        subscription.nextPhase?.product?.id ??
        subscription.nextPhase?.subscriptionProductId;
    const phaseEnd = subscription.currentPhase?.endDate;

    if (
        nextProductId != null &&
        productId != null &&
        nextProductId !== productId &&
        phaseEnd != null &&
        phaseEnd !== ''
    ) {
        return phaseEnd;
    }

    if (productId === SubscriptionProductId.TRIAL_PREMIUM) {
        return (
            subscription.currentPhase?.endDate ??
            subscription.nextPhase?.startDate ??
            null
        );
    }

    return null;
};

const getMembershipSubtitle = (
    subscription: StripeSubscriptionSummary,
): string | undefined => {
    const endDate = getMembershipEndDate(subscription);
    if (endDate == null || endDate === '') {
        return undefined;
    }

    const daysLeft = Math.max(
        0,
        differenceInCalendarDays(convertToDate(endDate), new Date()),
    );
    const dayLabel = daysLeft === 1 ? 'day' : 'days';

    return `${daysLeft} ${dayLabel} left · Ends on ${format(endDate, 'MMM d, yyyy')}`;
};

const emptyBillingHistoryCopy = (
    subscription: StripeSubscriptionSummary | null,
): string => {
    const productId = subscription?.product?.id ?? subscription?.type;

    if (productId === SubscriptionProductId.STANDARD) {
        return 'No membership fee.';
    }

    if (productId === SubscriptionProductId.TRIAL_PREMIUM) {
        return 'No charge during your trial.';
    }

    return 'No payments yet.';
};

export const Membership = () => {
    const user = useAuthedUser();
    const toast = useToastController();
    const queryClient = useQueryClient();
    const [isDeleteSheetOpen, setIsDeleteSheetOpen] = useState(false);
    const { data: subscription, isLoading: isSubscriptionLoading } =
        useGetSubscription({ userId: user.id });
    const {
        data: paymentMethod,
        isLoading: isLoadingPaymentMethod,
        refetch: refetchPaymentMethod,
    } = useGetPaymentMethod({ userId: user.id });
    const {
        invoices,
        nextCursor,
        isInitialLoading,
        isError,
        isFetching,
        refetch,
        loadMore,
    } = useBillingInvoicePages(user.id);

    const showInitialSpinner =
        isSubscriptionLoading || isInitialLoading || isLoadingPaymentMethod;
    const isStandard =
        subscription?.product?.id === SubscriptionProductId.STANDARD;
    const displayName = isStandard
        ? 'Fruitful Membership'
        : subscription?.product?.displayName;
    const membershipSubtitle =
        (subscription != null
            ? getMembershipSubtitle(subscription)
            : undefined) ??
        (isStandard
            ? `Member since ${format(getMemberSinceDate(user), 'MMM d, yyyy')}`
            : undefined);
    // Every other tier has a live paid subscription that needs a card on
    // file, so deleting (not just changing) is blocked there — mirrors the
    // backend's own check on the same catalog field.
    const canDeletePaymentMethod =
        subscription?.product?.isZeroDollarProduct === true;
    const hasPartnerMember = user.partnerMember != null;
    const { value: canManagePaymentMethod } = useFeatureGate(
        FeatureFlags.ENABLE_BILLING_SETTINGS,
    );

    return (
        <ScreenContainer title="Membership" hasBackButton={true}>
            {showInitialSpinner ? (
                <Spinner />
            ) : isError && invoices.length === 0 ? (
                <YStack gap={16}>
                    <BodyMediumRegular>
                        Could not load membership.
                    </BodyMediumRegular>
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.LARGE}
                        text="Try again"
                        onPress={() => {
                            void refetch();
                        }}
                    />
                </YStack>
            ) : (
                <YStack gap={32}>
                    <RowList rowHeaderProps={{ title: 'Your membership' }}>
                        <NavigationRow
                            title={displayName ?? 'Membership'}
                            subtitle={membershipSubtitle}
                            leftContentOverride={
                                <FruitfulCustomIcon
                                    icon="fruitful"
                                    color={isStandard ? 'green' : 'peach'}
                                    size="sm"
                                />
                            }
                            href="/settings/subscription"
                        />
                    </RowList>
                    {canManagePaymentMethod && (
                        <PaymentMethodActions
                            paymentMethod={paymentMethod ?? null}
                            canDelete={canDeletePaymentMethod}
                            showCardOwner={hasPartnerMember}
                            onPressDelete={() => {
                                setIsDeleteSheetOpen(true);
                            }}
                            onAttached={() => {
                                void refetchPaymentMethod();
                                void queryClient.invalidateQueries({
                                    queryKey: [`getSubscription/${user.id}`],
                                });
                            }}
                        />
                    )}
                    <YStack gap={16}>
                        <RowList rowHeaderProps={{ title: 'Billing history' }}>
                            {invoices.length === 0 ? (
                                <YStack padding={16}>
                                    <BodyMediumRegular>
                                        {emptyBillingHistoryCopy(
                                            subscription ?? null,
                                        )}
                                    </BodyMediumRegular>
                                </YStack>
                            ) : (
                                invoices.map((invoice) => (
                                    <BillingInvoiceRow
                                        key={invoice.id}
                                        invoice={invoice}
                                    />
                                ))
                            )}
                        </RowList>
                        {isError && (
                            <BodyMediumRegular>
                                Could not load more payments.
                            </BodyMediumRegular>
                        )}
                        {nextCursor != null && (
                            <Button
                                variant={ButtonVariant.SECONDARY}
                                size={ButtonSize.LARGE}
                                text="Load more"
                                disabled={isFetching}
                                onPress={loadMore}
                            />
                        )}
                    </YStack>
                </YStack>
            )}
            <PaymentMethodDeleteSheet
                isOpen={isDeleteSheetOpen}
                paymentMethod={paymentMethod ?? null}
                onClose={() => {
                    setIsDeleteSheetOpen(false);
                }}
                onDeleted={() => {
                    setIsDeleteSheetOpen(false);
                    void refetchPaymentMethod();
                    toast.show('Payment method deleted', {
                        duration: SUCCESS_TOAST_DURATION,
                        icon: SuccessToastIcon,
                    });
                }}
            />
        </ScreenContainer>
    );
};
